import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "../../api-utility/axiosInstance";

// FETCH ALL LEADS
export const fetchAllLeads = createAsyncThunk(
  "leads/getAll",
  async (params, { signal, rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/leads", {
        params,
        signal,
      });

      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch leads",
      );
    }
  },
);

// FETCH LEAD STATS (KPI cards, pipeline, quick view counts)
export const fetchLeadStats = createAsyncThunk(
  "leads/fetchStats",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/leads/stats", { params });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch lead stats",
      );
    }
  },
);

// FETCH LEAD LIST (MINIMAL FOR DROPDOWNS)
export const fetchLeadList = createAsyncThunk(
  "leads/getLeadList",
  async (params, { signal, rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/leads/lead-list", {
        params: {
          order_by: "created_at",
          order: "desc",
          ...params,
        },
        signal,
      });

      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch lead list",
      );
    }
  },
);

// FETCH LEAD SIMPLIFIED LIST
export const fetchLeadSimplifiedList = createAsyncThunk(
  "leads/fetchLeadSimplifiedList",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/leads/lead-simplified-list`, {
        params,
      });

      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch lead simplified list",
      );
    }
  },
);

// CREATE LEAD
export const addLead = createAsyncThunk(
  "leads/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/leads", payload);

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// FETCH SINGLE LEAD
export const getLeadById = createAsyncThunk(
  "leads/getById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/leads/${id}`);

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch lead",
      );
    }
  },
);

// UPDATE LEAD
export const updateLead = createAsyncThunk(
  "leads/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.put(`/tenant/leads/${id}`, payload);

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// BULK DELETE LEADS
export const bulkDeleteLeads = createAsyncThunk(
  "leads/bulkDelete",
  async (leadIds, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/tenant/leads/bulk-delete`, {
        data: { ids: leadIds },
      });
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// DELETE LEAD BY
export const deleteLead = createAsyncThunk(
  "leads/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/tenant/leads/${id}`);
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete lead");
    }
  },
);

// FORWARD LEAD to another user with a reason ({ id, assigned_to, note })
export const forwardLead = createAsyncThunk(
  "leads/forward",
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.post(`/tenant/leads/${id}/forward`, payload);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to forward lead");
    }
  },
);

// FORWARD HISTORY of a lead (newest first)
export const getLeadForwards = createAsyncThunk(
  "leads/forwards",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/leads/${id}/forwards`);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to fetch forward history");
    }
  },
);

//LEAD NOTES
export const addLeadNote = createAsyncThunk(
  "leads/addNote",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/lead-notes", payload);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to add note");
    }
  },
);

// UPDATE LEAD NOTE
export const updateLeadNote = createAsyncThunk(
  "leads/updateNote",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.put(`/tenant/lead-notes/${id}`, payload);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to update note");
    }
  },
);

// GET LEAD NOTE
export const getLeadNote = createAsyncThunk(
  "leads/getNote",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/lead-notes?lead_id=${id}`);

      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to fetch note");
    }
  },
);

// DELETE LEAD NOTE
export const deleteLeadNote = createAsyncThunk(
  "leads/deleteNote",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/tenant/lead-notes/${id}`);
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to delete note");
    }
  },
);

// LEAD FILES
export const addLeadFile = createAsyncThunk(
  "leads/addFile",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await Axios.post("/tenant/lead-files", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to upload file");
    }
  },
);

// DELETE LEAD FILE
export const deleteLeadFile = createAsyncThunk(
  "leads/deleteFile",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/tenant/lead-files/${id}`);
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to delete file");
    }
  },
);

// GET LEAD HISTORY
export const getLeadHistory = createAsyncThunk(
  "leads/getHistory",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/lead-histories?lead_id=${id}`);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to fetch history");
    }
  },
);

// ADD LEAD HISTORY
export const addLeadHistory = createAsyncThunk(
  "leads/addHistory",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/lead-histories", payload);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to add history");
    }
  },
);

// TOGGLE LEAD ACTIVE STATUS
export const toggleLeadActive = createAsyncThunk(
  "leads/toggleActive",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.post(`/tenant/leads/${id}/change-status`);
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to change status");
    }
  },
);

// CHANGE LEAD PIPELINE STATUS
export const changeLeadStatus = createAsyncThunk(
  "leads/changeLeadStatus",
  async ({ id, statusId, note }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `/tenant/leads/${id}/change-lead-status`,
        {
          lead_status_id: statusId,
          note: note,
        },
      );
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to change lead status",
      );
    }
  },
);

// PATCH UPDATE LEAD
export const patchUpdateLead = createAsyncThunk(
  "leads/patchUpdate",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.patch(`/tenant/leads/${id}`, payload);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to update lead");
    }
  },
);

// CHANGE LEAD SOURCE
export const changeLeadSource = createAsyncThunk(
  "leads/changeLeadSource",
  async ({ id, sourceId }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(`/tenant/leads/${id}/change-source`, {
        lead_source_id: sourceId,
      });
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to change lead source",
      );
    }
  },
);

// LEAD TAGS
export const fetchLeadTags = createAsyncThunk(
  "leads/fetchLeadTags",
  async (leadId, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/lead-tags", {
        params: { lead_id: leadId },
      });
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || "Failed to fetch lead tags",
      );
    }
  },
);

// ADD LEAD TAG
export const addLeadTag = createAsyncThunk(
  "leads/addLeadTag",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/lead-tags", payload);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to add lead tag");
    }
  },
);

// DELETE LEAD TAG
export const deleteLeadTag = createAsyncThunk(
  "leads/deleteLeadTag",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/tenant/lead-tags/${id}`);
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to delete lead tag",
      );
    }
  },
);

// Configuration Data Listing
export const fetchConfigurationData = createAsyncThunk(
  "configuration/fetchData",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/leads/configurations", { params });
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to fetch data");
    }
  },
);

const leadsSlice = createSlice({
  name: "leads",
  initialState: {
    leads: [],
    leadList: [],
    selectedLead: null,
    total_records: 0,
    isLeadsLoading: true,
    stats: null,
    isStatsLoading: false,
    isConfigLoading: false,
    error: null,
    isBulkDeleting: false,
    leadNotes: [],
    leadHistories: [],
    configurationData: {
      assigned_to: [],
      followup_assigned_to: [],
      products: [],
      lead_sources: [],
      lead_statuses: [],
      followup_types: [],
      followup_statuses: [],
      priorities: [],
    },
    currentRequestId: null,
  },

  extraReducers: (builder) => {
    builder
      // LEAD STATS
      .addCase(fetchLeadStats.pending, (state) => {
        state.isStatsLoading = true;
      })
      .addCase(fetchLeadStats.fulfilled, (state, action) => {
        state.isStatsLoading = false;
        state.stats = action.payload || state.stats;
      })
      .addCase(fetchLeadStats.rejected, (state) => {
        state.isStatsLoading = false;
      })

      //GET ALL LEADS
      .addCase(fetchAllLeads.pending, (state, action) => {
        state.isLeadsLoading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchAllLeads.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.isLeadsLoading = false;
        state.leads = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(fetchAllLeads.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.isLeadsLoading = false;
        state.error = action.payload || "Failed to fetch leads";
      })

      // GET MINIMAL LEADS
      .addCase(fetchLeadList.pending, (state, action) => {
        state.isLeadsLoading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchLeadList.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.isLeadsLoading = false;
        state.leadList = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(fetchLeadList.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.isLeadsLoading = false;
        state.error = action.payload || "Failed to fetch leads";
      })

      // Configuration Data Listing
      .addCase(fetchConfigurationData.pending, (state) => {
        state.isConfigLoading = true;
        state.error = null;
      })
      .addCase(fetchConfigurationData.fulfilled, (state, action) => {
        state.isConfigLoading = false;
        state.configurationData = action.payload;
      })
      .addCase(fetchConfigurationData.rejected, (state, action) => {
        state.isConfigLoading = false;
        state.error = action.payload || "Failed to fetch data";
      })

      // GET SINGLE LEAD
      .addCase(getLeadById.pending, (state) => {
        state.isLeadsLoading = true;
        state.selectedLead = null;
      })
      .addCase(getLeadById.fulfilled, (state, action) => {
        state.isLeadsLoading = false;
        state.selectedLead = action.payload;
      })
      .addCase(getLeadById.rejected, (state, action) => {
        state.isLeadsLoading = false;
        state.error = action.payload;
      })

      // LEAD NOTES
      .addCase(getLeadNote.pending, (state) => {
        state.isLeadsLoading = true;
        state.leadNotes = [];
      })
      .addCase(getLeadNote.fulfilled, (state, action) => {
        state.isLeadsLoading = false;
        state.leadNotes = action.payload;
      })
      .addCase(getLeadNote.rejected, (state, action) => {
        state.isLeadsLoading = false;
        state.error = action.payload;
      })

      // LEAD HISTORY
      .addCase(getLeadHistory.pending, (state) => {
        state.isLeadsLoading = true;
        state.leadHistories = [];
      })
      .addCase(getLeadHistory.fulfilled, (state, action) => {
        state.isLeadsLoading = false;
        state.leadHistories = action.payload;
      })
      .addCase(getLeadHistory.rejected, (state, action) => {
        state.isLeadsLoading = false;
        state.error = action.payload;
      });
  },
});

export default leadsSlice.reducer;
